import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { LiveGameRouteSkeletonWithHud } from "@/components/molecules/live-game-route-skeleton";
import { GameInstructionsSkeleton } from "@/section/instructions/instructions-skeleton";
import { MazePlayMode } from "@/utils/maze/maze-play-mode";
import { getConfiguredUiStageId } from "@/utils/stage/stage-utils";
import { paths } from "@app/router/routes";

import HomePage from "@/pages/home-page";
// Demo/Game/Result are the offline-critical gameplay flow, so they're bundled
// eagerly instead of behind React.lazy — a lazy chunk fetched for the first
// time after connectivity drops fails outright, which is exactly what made
// asset-manifest.ts's upfront preload unreliable (see preloadAllAssets).
import DemoPage from "@/pages/demo-page";
import GamePage from "@/pages/game-page";
import ResultPage from "@/pages/result-page";
// Preview is admin-only and already requires a live backend, so it stays lazy.
const PreviewPage = lazy(() => import("@/pages/preview-page"));
const NotFoundPage = lazy(() => import("@/pages/not-found-page"));

const uiStageId = getConfiguredUiStageId();

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path={paths.home}
          element={<HomePage />}
        />
        <Route
          path={paths.demo}
          element={<DemoPage />}
        />
        <Route
          path={paths.game}
          element={<GamePage />}
        />
        <Route
          path={paths.preview}
          element={
            <Suspense
              fallback={
                <LiveGameRouteSkeletonWithHud playMode={MazePlayMode.Preview} />
              }
            >
              <PreviewPage />
            </Suspense>
          }
        />
        <Route
          path={paths.results}
          element={<ResultPage />}
        />
        <Route
          path="/404"
          element={
            <Suspense fallback={<GameInstructionsSkeleton stage={uiStageId} />}>
              <NotFoundPage />
            </Suspense>
          }
        />
        <Route path="*" element={<Navigate to={paths.home} replace />} />
      </Routes>
    </BrowserRouter>
  );
}
